/**
 * RFIs List Page
 * BuilderBel Construction Project Management System
 */
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileQuestion,
  FileText,
  Inbox,
  Pencil,
  Plus,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { RFIDrawer } from '../components/rfis';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  PriorityBadge,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  downloadRFIPdf,
  useDeleteRFIMutation,
  useGetRFIsQuery,
  useGetRFIStatsQuery,
  useSubmitRFIMutation,
} from '../services/rfiApi';
import type { RFI, RFIDiscipline, RFIPriority, RFIStatus } from '../types/rfi';

// Status filter options - matches backend enum
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'pending_response', label: 'Pending Response' },
  { value: 'answered', label: 'Answered' },
  { value: 'closed', label: 'Closed' },
  { value: 'closed_void', label: 'Void' },
];

// Priority filter options
const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Discipline filter options
const DISCIPLINE_OPTIONS = [
  { value: '', label: 'All Disciplines' },
  { value: 'architectural', label: 'Architectural' },
  { value: 'structural', label: 'Structural' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'civil', label: 'Civil' },
  { value: 'general', label: 'General' },
];

// Sort options
const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'rfiNumber', label: 'RFI Number' },
  { value: 'priority', label: 'Priority' },
  { value: 'status', label: 'Status' },
];

// Get status variant for StatusBadge
const getStatusVariant = (
  status: RFIStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'closed':
      return 'success';
    case 'answered':
      return 'info';
    case 'pending_response':
    case 'open':
      return 'warning';
    case 'closed_void':
      return 'error';
    case 'draft':
    default:
      return 'neutral';
  }
};

// Get status label for display
const getStatusLabel = (status: RFIStatus): string => {
  const labels: Record<RFIStatus, string> = {
    draft: 'Draft',
    open: 'Open',
    pending_response: 'Pending',
    answered: 'Answered',
    closed: 'Closed',
    closed_void: 'Void',
  };
  return labels[status] || status;
};

// Get priority variant for PriorityBadge
const getPriorityVariant = (priority: RFIPriority): 'low' | 'medium' | 'high' | 'critical' => {
  return priority;
};

// Format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const RFIs: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // State for filters and pagination
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<RFIStatus | ''>('');
  const [priority, setPriority] = useState<RFIPriority | ''>('');
  const [discipline, setDiscipline] = useState<RFIDiscipline | ''>('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // State for drawer
  const [selectedRFI, setSelectedRFI] = useState<RFI | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // State for dropdown menu
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API calls
  const {
    data: rfisResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetRFIsQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        priority: priority || undefined,
        discipline: discipline || undefined,
        sortBy: sortBy as 'createdAt' | 'dueDate' | 'rfiNumber' | 'priority' | 'status',
        sortOrder,
      },
    },
    { skip: !projectId }
  );

  const { data: statsResponse } = useGetRFIStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteRFI, { isLoading: isDeleting }] = useDeleteRFIMutation();
  const [submitRFI] = useSubmitRFIMutation();

  // Extract data
  const rfis = useMemo(() => rfisResponse?.data?.rfis || [], [rfisResponse]);
  const pagination = rfisResponse?.data?.pagination;
  const stats = statsResponse?.data;

  // Computed stats for header
  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Total',
        value: stats.total || 0,
        variant: 'brand' as const,
      },
      {
        title: 'Open',
        value: (stats.open || 0) + (stats.pendingResponse || 0),
        variant: 'warning' as const,
      },
      {
        title: 'Answered',
        value: stats.answered || 0,
        variant: 'success' as const,
      },
      {
        title: 'Overdue',
        value: stats.overdue || 0,
        variant: 'danger' as const,
      },
    ];
  }, [stats]);

  // Handlers
  const handleCreateRFI = useCallback(() => {
    setSelectedRFI(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleViewRFI = useCallback((rfi: RFI) => {
    setSelectedRFI(rfi);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleEditRFI = useCallback((rfi: RFI) => {
    setSelectedRFI(rfi);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedRFI(null);
  }, []);

  const handleSubmitRFI = useCallback(
    async (rfi: RFI) => {
      if (!projectId) return;
      try {
        await submitRFI({
          projectId,
          rfiId: rfi._id,
        }).unwrap();
        toast.success('RFI submitted successfully');
        refetch();
      } catch {
        toast.error('Failed to submit RFI');
      }
    },
    [projectId, submitRFI, toast, refetch]
  );

  const handleDeleteRFI = useCallback(
    async (rfi: RFI) => {
      if (!projectId) return;
      if (window.confirm(`Are you sure you want to delete ${rfi.rfiNumber}?`)) {
        try {
          await deleteRFI({
            projectId,
            rfiId: rfi._id,
          }).unwrap();
          toast.success('RFI deleted successfully');
        } catch {
          toast.error('Failed to delete RFI');
        }
      }
    },
    [projectId, deleteRFI, toast]
  );

  const handleDownloadPDF = useCallback(
    async (rfi: RFI) => {
      if (!projectId) return;
      try {
        await downloadRFIPdf(projectId, rfi._id, rfi.rfiNumber);
        toast.success('PDF downloaded successfully');
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Render filters
  const renderFilters = () => (
    <div className="flex items-center gap-2">
      <SearchableDropdown
        placeholder="Status"
        value={status}
        onChange={value => {
          setStatus(value as RFIStatus | '');
          setPage(1);
        }}
        options={STATUS_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Priority"
        value={priority}
        onChange={value => {
          setPriority(value as RFIPriority | '');
          setPage(1);
        }}
        options={PRIORITY_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Discipline"
        value={discipline}
        onChange={value => {
          setDiscipline(value as RFIDiscipline | '');
          setPage(1);
        }}
        options={DISCIPLINE_OPTIONS}
        searchable={false}
      />
    </div>
  );

  // Render pagination
  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const { page: currentPage, pages: totalPages, total } = pagination;
    const startItem = (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Showing {startItem}-{endItem} of {total} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Render row actions dropdown using portal-based ActionMenu
  const renderRowActions = (rfi: RFI) => {
    const isMenuOpen = activeMenuId === rfi._id;

    const menuItems: (ActionMenuItem | 'divider')[] = [
      // View & Edit
      {
        label: 'View Details',
        icon: <Eye className="h-3.5 w-3.5" />,
        onClick: () => handleViewRFI(rfi),
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onClick: () => handleEditRFI(rfi),
        hidden: rfi.status !== 'draft',
      },
      'divider',
      // Status Actions
      {
        label: 'Submit RFI',
        icon: <Send className="h-3.5 w-3.5" />,
        onClick: () => handleSubmitRFI(rfi),
        hidden: rfi.status !== 'draft',
      },
      {
        label: 'Respond',
        icon: <FileText className="h-3.5 w-3.5" />,
        onClick: () => {
          setSelectedRFI(rfi);
          setDrawerMode('view');
          setIsDrawerOpen(true);
        },
        hidden: rfi.status !== 'open' && rfi.status !== 'pending_response',
      },
      {
        label: 'Close RFI',
        icon: <Check className="h-3.5 w-3.5" />,
        onClick: () => {
          setSelectedRFI(rfi);
          setDrawerMode('view');
          setIsDrawerOpen(true);
        },
        variant: 'success',
        hidden: rfi.status !== 'answered',
      },
      {
        label: 'Void RFI',
        icon: <XCircle className="h-3.5 w-3.5" />,
        onClick: () => {
          setSelectedRFI(rfi);
          setDrawerMode('view');
          setIsDrawerOpen(true);
        },
        variant: 'warning',
        hidden: rfi.status === 'closed' || rfi.status === 'closed_void',
      },
      'divider',
      // PDF Export
      {
        label: 'Download PDF',
        icon: <Download className="h-3.5 w-3.5" />,
        onClick: () => handleDownloadPDF(rfi),
      },
      'divider',
      // Delete
      {
        label: 'Delete',
        icon: <Trash2 className="h-3.5 w-3.5" />,
        onClick: () => handleDeleteRFI(rfi),
        variant: 'danger',
        disabled: isDeleting,
        hidden: rfi.status !== 'draft',
      },
    ];

    return (
      <ActionMenu
        id={rfi._id}
        items={menuItems}
        isOpen={isMenuOpen}
        onOpenChange={open => setActiveMenuId(open ? rfi._id : null)}
      />
    );
  };

  // Show loading state
  if (isLoading && rfis.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Show project selection required
  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <EmptyState
          icon={Inbox}
          title="No Project Selected"
          description="Please select a project to view RFIs."
        />
      </div>
    );
  }

  return (
    <ModuleLayout
      title="Requests for Information"
      description="Manage RFIs and communication with architects and engineers"
      icon={<FileQuestion className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New RFI',
        onClick: handleCreateRFI,
        icon: <Plus className="h-3 w-3 mr-1" />,
      }}
      stats={headerStats}
      searchValue={search}
      onSearchChange={value => {
        setSearch(value);
        setPage(1);
      }}
      filters={renderFilters()}
      sortOptions={SORT_OPTIONS}
      sortValue={sortBy}
      onSortChange={setSortBy}
      sortOrder={sortOrder}
      onSortOrderChange={setSortOrder}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={renderPagination()}
    >
      {rfis.length === 0 ? (
        <EmptyState
          icon={FileQuestion}
          title="No RFIs found"
          description={
            search || status || priority || discipline
              ? "Try adjusting your filters to find what you're looking for."
              : 'Get started by creating your first RFI.'
          }
          action={
            !search && !status && !priority && !discipline
              ? {
                  label: 'Create RFI',
                  onClick: handleCreateRFI,
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  RFI #
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discipline
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ball In Court
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rfis.map(rfi => (
                <tr
                  key={rfi._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewRFI(rfi)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{rfi.rfiNumber}</span>
                      {rfi.isOverdue && (
                        <span title="Overdue">
                          <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900 truncate max-w-[250px] block">
                      {rfi.subject}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={getStatusLabel(rfi.status)}
                      variant={getStatusVariant(rfi.status)}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <PriorityBadge priority={getPriorityVariant(rfi.priority)} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-500 capitalize">{rfi.discipline}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{rfi.ballInCourtName || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm ${
                        rfi.isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'
                      }`}
                    >
                      {formatDate(rfi.dueDate)}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {renderRowActions(rfi)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* RFI Drawer */}
      <RFIDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        rfi={selectedRFI}
        mode={drawerMode}
        onSuccess={refetch}
      />
    </ModuleLayout>
  );
};

export default RFIs;
